import { Link } from "react-router-dom";
import { CodeBlock } from "../components/CodeBlock";
import { SEO } from "../components/SEO";

const CLAUDE_CONFIG = `{
  "mcpServers": {
    "starsinger": {
      "command": "npx",
      "args": ["-y", "starsinger-mcp"],
      "env": {
        "STARSINGER_API_KEY": "sk_free_..."
      }
    }
  }
}`;

const CURSOR_CONFIG = `{
  "mcpServers": {
    "starsinger": {
      "command": "npx",
      "args": ["-y", "starsinger-mcp"],
      "env": {
        "STARSINGER_API_KEY": "sk_free_..."
      }
    }
  }
}`;

const VSCODE_CONFIG = `{
  "mcp": {
    "servers": {
      "starsinger": {
        "command": "npx",
        "args": ["-y", "starsinger-mcp"],
        "env": {
          "STARSINGER_API_KEY": "sk_free_..."
        }
      }
    }
  }
}`;

const SEARCH_EXAMPLE = `{
  "tracks": [
    {
      "id": "jd7f8a9b2c3e4f5a",
      "title": "Midnight Sessions",
      "songTitle": "Midnight Sessions",
      "artistName": "Miles Rivers",
      "genre": "jazz",
      "mood": "purple",
      "audioUrl": "https://cdn.starsinger.ai/tracks/midnight-sessions.mp3",
      "thumbnailUrl": "https://cdn.starsinger.ai/covers/midnight-sessions.jpg",
      "lyrics": "Under the city lights, where the music plays...",
      "language": "en",
      "views": 1240,
      "likes": 89,
      "source": "StarSinger",
      "createYourOwn": "https://starsinger.ai"
    }
  ],
  "count": 1
}`;

const TRACK_EXAMPLE = `{
  "id": "jd7f8a9b2c3e4f5a",
  "title": "Midnight Sessions",
  "songTitle": "Midnight Sessions",
  "artistName": "Miles Rivers",
  "genre": "jazz",
  "mood": "purple",
  "audioUrl": "https://cdn.starsinger.ai/tracks/midnight-sessions.mp3",
  "thumbnailUrl": "https://cdn.starsinger.ai/covers/midnight-sessions.jpg",
  "lyrics": "Under the city lights, where the music plays...",
  "language": "en",
  "views": 1240,
  "likes": 89,
  "source": "StarSinger",
  "createYourOwn": "https://starsinger.ai"
}`;

const ARTISTS_EXAMPLE = `{
  "artists": [
    {
      "id": "a1b2c3d4e5f6",
      "name": "Miles Rivers",
      "handle": "miles_rivers",
      "genre": "jazz",
      "bio": "Smooth jazz pianist exploring the intersection of tradition and AI...",
      "songCount": 42,
      "thumbnailUrl": "https://cdn.starsinger.ai/artists/miles-rivers.jpg"
    }
  ],
  "count": 1
}`;

const MOODS_EXAMPLE = `{
  "moods": [
    { "id": "purple", "name": "Chill", "description": "Relaxed and ambient vibes" },
    { "id": "red", "name": "Energetic", "description": "High energy party music" },
    { "id": "blue", "name": "Emotional", "description": "Deep and emotional songs" },
    { "id": "gold", "name": "Uplifting", "description": "Happy and uplifting tunes" },
    { "id": "green", "name": "Nature", "description": "Peaceful natural sounds" },
    { "id": "pink", "name": "Romantic", "description": "Love songs and romance" },
    { "id": "dark", "name": "Intense", "description": "Dark and intense vibes" },
    { "id": "rainbow", "name": "Eclectic", "description": "Experimental and unique" }
  ]
}`;

interface ToolDoc {
  name: string;
  description: string;
  restPath: string;
  curlExample: string;
  responseExample: string;
  args: { name: string; type: string; required: boolean; description: string }[];
}

const tools: ToolDoc[] = [
  {
    name: "search_music",
    description:
      "Search the catalog by natural language query, mood, or genre. Returns matching tracks with streaming URLs.",
    restPath: "/api/mcp/search?q=chill+jazz&limit=3",
    curlExample: `curl "https://mcp-api.starsinger.ai/api/mcp/search?q=chill+jazz&limit=3" \\
  -H "Authorization: Bearer sk_free_..."`,
    responseExample: SEARCH_EXAMPLE,
    args: [
      { name: "query", type: "string", required: true, description: "Natural language search query" },
      {
        name: "mood",
        type: "enum",
        required: false,
        description: "Filter by mood: purple (chill), red (energetic), blue (emotional), gold (uplifting), green (nature), pink (romantic), dark (intense), rainbow (eclectic)",
      },
      { name: "limit", type: "number", required: false, description: "Results to return (default 10, max 50)" },
      { name: "lang", type: "string", required: false, description: "Listener language, ISO 639-1. Songs sung in it and instrumentals rank first." },
      { name: "instrumental", type: "boolean", required: false, description: "true = instrumentals only" },
      { name: "exclude", type: "string[]", required: false, description: "Track ids already played; skipped along with their duplicates" },
    ],
  },
  {
    name: "similar_music",
    description: "More tracks like a given track (same genre, then subgenre, mood and vocal style). Use for \"next song\" or \"something similar\".",
    restPath: "/api/mcp/similar?id=TRACK_ID&limit=5",
    curlExample: `curl "https://mcp-api.starsinger.ai/api/mcp/similar?id=TRACK_ID&limit=5" \\
  -H "Authorization: Bearer sk_free_..."`,
    responseExample: SEARCH_EXAMPLE,
    args: [
      { name: "trackId", type: "string", required: true, description: "Track id from search results" },
      { name: "limit", type: "number", required: false, description: "Number of tracks (default 10, max 50)" },
      { name: "lang / instrumental / exclude", type: "—", required: false, description: "Same as search_music" },
    ],
  },
  {
    name: "get_track",
    description: "Get full details and streaming URL for a specific track, including complete lyrics and artist info.",
    restPath: "/api/mcp/track?id=jd7f8a9b2c3e4f5a",
    curlExample: `curl "https://mcp-api.starsinger.ai/api/mcp/track?id=TRACK_ID" \\
  -H "Authorization: Bearer sk_free_..."`,
    responseExample: TRACK_EXAMPLE,
    args: [
      { name: "trackId", type: "string", required: true, description: "Track ID from search results" },
    ],
  },
  {
    name: "get_trending",
    description: "Get currently popular tracks ranked by engagement.",
    restPath: "/api/mcp/trending?limit=5",
    curlExample: `curl "https://mcp-api.starsinger.ai/api/mcp/trending?limit=5" \\
  -H "Authorization: Bearer sk_free_..."`,
    responseExample: SEARCH_EXAMPLE,
    args: [
      { name: "limit", type: "number", required: false, description: "Number of tracks (default 10, max 50)" },
    ],
  },
  {
    name: "browse_artists",
    description: "Browse AI catalog artists with optional genre filter. Returns artist profiles with bios and song counts.",
    restPath: "/api/mcp/artists?genre=jazz&limit=5",
    curlExample: `curl "https://mcp-api.starsinger.ai/api/mcp/artists?genre=jazz" \\
  -H "Authorization: Bearer sk_free_..."`,
    responseExample: ARTISTS_EXAMPLE,
    args: [
      { name: "genre", type: "string", required: false, description: 'Filter by genre (e.g. "jazz", "classical", "pop")' },
      { name: "limit", type: "number", required: false, description: "Number of artists (default 10, max 50)" },
    ],
  },
  {
    name: "get_artist",
    description: "Get an artist's full profile and their complete discography with streaming URLs.",
    restPath: "/api/mcp/artist?handle=miles_rivers",
    curlExample: `curl "https://mcp-api.starsinger.ai/api/mcp/artist?handle=miles_rivers" \\
  -H "Authorization: Bearer sk_free_..."`,
    responseExample: TRACK_EXAMPLE,
    args: [
      { name: "artistId", type: "string", required: false, description: "Artist ID from browse results" },
      { name: "handle", type: "string", required: false, description: 'Artist handle (e.g. "miles_rivers")' },
    ],
  },
];

interface SongToolDoc {
  name: string;
  description: string;
  rest: string;
  args: { name: string; type: string; description: string }[];
}

const songTools: SongToolDoc[] = [
  {
    name: "make_song",
    description:
      "Make a finished song from a brief or from the user's lyrics: written, composed and sung with real vocals, or an instrumental. 49 languages. Returns a job — poll song_status until it is done, then give the user the song link.",
    rest: "POST /api/mcp/songs",
    args: [
      { name: "brief", type: "string", description: "What the song is about, who it is for, the feel. Required unless lyrics are given." },
      { name: "lyrics", type: "string", description: "Complete lyrics to sing, if the user wrote them." },
      { name: "genre", type: "string", description: "One genre label: Pop, Jazz, Hip-Hop, Country, Lo-fi, Classical…" },
      { name: "style", type: "string", description: "Production values: tempo, mood, instruments, era. Under 12 words." },
      { name: "language", type: "string", description: "ISO 639-1 code of the lyrics (default en)." },
      { name: "voice", type: "enum", description: "male | female | neutral" },
      { name: "instrumental", type: "boolean", description: "No vocals at all." },
      { name: "singInMyVoice", type: "boolean", description: "Sing in the account owner's own voice, if they set one up." },
      { name: "title", type: "string", description: "Optional song title." },
      { name: "publicOk", type: "boolean", description: "The user agreed that a FREE song is published to the public feed. Required for a free song." },
    ],
  },
  {
    name: "song_status",
    description: "Status of a make_song job: writing → generating → done (with the song's link and MP3), or cap_reached / needs_public_ok / failed. Nothing is spent on a failed song.",
    rest: "GET /api/mcp/songs?id=JOB_ID",
    args: [{ name: "jobId", type: "string", description: "The jobId returned by make_song." }],
  },
  {
    name: "my_songs",
    description: "The account's most recent songs with playable MP3 links and their song ids.",
    rest: "GET /api/mcp/my-songs?limit=10",
    args: [{ name: "limit", type: "number", description: "1–50." }],
  },
  {
    name: "wallet",
    description: "What the next make_song will spend: today's free song, a purchased credit, or nothing left (then the checkout link and price).",
    rest: "GET /api/mcp/wallet",
    args: [],
  },
  {
    name: "make_music_video",
    description:
      "Turn one of the account's songs into a 2–3 minute music video in which a real person — the user, or an adult who agreed — sings it, made from one photo. Returns the StarSinger link where the user adds the photo and pays (or spends an existing music video credit).",
    rest: "GET /api/mcp/music-video?songId=SONG_ID&mode=make",
    args: [{ name: "songId", type: "string", description: "From make_song / song_status or my_songs." }],
  },
  {
    name: "music_video_status",
    description: "The music video for one of the account's songs: not_started, needs_photo_and_payment, rendering (with progress), done (with the video link), or failed (credit returned).",
    rest: "GET /api/mcp/music-video?songId=SONG_ID&mode=status",
    args: [{ name: "songId", type: "string", description: "The song's id." }],
  },
];

export function Docs() {
  return (
    <>
      <SEO
        title="Documentation | StarSinger MCP"
        description="StarSinger MCP documentation: connect the hosted server with OAuth or run the npm package, then make songs and music videos and search the AI music catalog from any MCP client."
        pathname="/docs"
      />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Documentation</h1>
      <p className="text-text-muted mb-10">
        Let any MCP client make a finished, sung song, turn it into a music video starring the user, and search the
        StarSinger catalog.
      </p>

      {/* Quick Start */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="quickstart">
          Quick Start
        </h2>
        <ol className="space-y-4 text-sm text-text-muted leading-relaxed">
          <li>
            <strong className="text-text">1. Connect.</strong> Either add the hosted server as a custom connector —{" "}
            <code className="text-xs bg-surface px-1 py-0.5 rounded">https://mcp-api.starsinger.ai/mcp</code> — and sign
            in with a StarSinger account (<a href="#hosted" className="text-brand hover:underline">OAuth</a>), or{" "}
            <a href="#getting-api-key" className="text-brand hover:underline">get an API key</a> and run the npm package.
          </li>
          <li>
            <strong className="text-text">2. Ask your agent</strong> — Try: "Make a song for my sister's birthday, upbeat
            pop" or "Find me some chill jazz piano tracks".
          </li>
          <li>
            <strong className="text-text">3. Money and consent are handled for you.</strong> The tools tell your agent
            when a song would be public or would cost money, and hand back a StarSinger link for anything paid —{" "}
            <a href="#songs" className="text-brand hover:underline">details</a>.
          </li>
        </ol>
      </section>

      {/* Hosted server */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="hosted">
          Hosted Server (OAuth)
        </h2>
        <p className="text-sm text-text-muted mb-4">
          The recommended way to connect an assistant: nothing to install, and each user signs in with their own
          StarSinger account, so songs, credits and videos land on that account.
        </p>
        <div className="border border-border rounded-xl overflow-hidden mb-4">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              <tr><td className="px-4 py-3 text-text-muted w-40">Endpoint</td><td className="px-4 py-3 font-mono text-xs">https://mcp-api.starsinger.ai/mcp</td></tr>
              <tr><td className="px-4 py-3 text-text-muted">Transport</td><td className="px-4 py-3 text-xs">Streamable HTTP, stateless</td></tr>
              <tr><td className="px-4 py-3 text-text-muted">Auth</td><td className="px-4 py-3 text-xs">OAuth 2.1 authorization code + PKCE (S256), dynamic client registration, refresh tokens — or <code className="bg-surface px-1 py-0.5 rounded">Authorization: Bearer &lt;API key&gt;</code></td></tr>
              <tr><td className="px-4 py-3 text-text-muted">Metadata</td><td className="px-4 py-3 font-mono text-xs break-all">https://mcp-api.starsinger.ai/.well-known/oauth-authorization-server</td></tr>
              <tr><td className="px-4 py-3 text-text-muted">Scopes</td><td className="px-4 py-3 text-xs"><code className="bg-surface px-1 py-0.5 rounded">songs</code> (make and list the user&apos;s songs and videos) · <code className="bg-surface px-1 py-0.5 rounded">catalog</code> (search and stream)</td></tr>
              <tr><td className="px-4 py-3 text-text-muted">Tools</td><td className="px-4 py-3 font-mono text-xs">make_song, song_status, my_songs, wallet, make_music_video, music_video_status, search_music, similar_music</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-text-muted">
          Users can see and disconnect connected apps at any time in Settings on starsinger.ai.
        </p>
      </section>

      {/* Songs and music videos */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="songs">
          Making Songs and Music Videos
        </h2>
        <div className="border border-border rounded-xl p-5 bg-surface-light mb-6 text-sm text-text-muted space-y-2">
          <p><strong className="text-text">Money and consent — what the tools enforce:</strong></p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Every account gets <strong className="text-text">one free song a day</strong>. A free song is <strong className="text-text">published to the public StarSinger feed</strong>, so <code className="text-xs bg-surface px-1 py-0.5 rounded">make_song</code> returns <code className="text-xs bg-surface px-1 py-0.5 rounded">needs_public_ok</code> until the agent has told the user and passes <code className="text-xs bg-surface px-1 py-0.5 rounded">publicOk: true</code>.</li>
            <li>After that, a purchased song credit ($1.99 in the US) is used, and the song stays private. With none left the result carries a checkout link and the price — never invent one.</li>
            <li>The music video ($19.99 in the US) needs a photo of the person who stars — the user, or an adult who agreed — and payment. Neither can travel in a tool call, so <code className="text-xs bg-surface px-1 py-0.5 rounded">make_music_video</code> returns a StarSinger link where the user does both. The user is emailed when the video is ready.</li>
            <li>Nothing is ever charged inside the chat, and there is no subscription.</li>
          </ul>
        </div>

        <div className="space-y-4">
          {songTools.map((tool) => (
            <div key={tool.name} className="border border-border rounded-xl p-5 bg-surface-light">
              <h3 className="font-mono text-brand font-semibold mb-2">{tool.name}</h3>
              <p className="text-sm text-text-muted mb-3">{tool.description}</p>
              {tool.args.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden mb-3">
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-border">
                      {tool.args.map((arg) => (
                        <tr key={arg.name}>
                          <td className="px-3 py-2 font-mono text-xs w-36">{arg.name}</td>
                          <td className="px-3 py-2 text-xs text-text-muted w-20">{arg.type}</td>
                          <td className="px-3 py-2 text-xs text-text-muted">{arg.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-text-muted">REST: <code className="bg-surface px-1 py-0.5 rounded">{tool.rest}</code></p>
            </div>
          ))}
        </div>
      </section>

      {/* Getting Your API Key */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="getting-api-key">
          Getting an API Key (npm package / REST)
        </h2>
        <div className="border border-border rounded-xl p-5 bg-surface-light">
          <ol className="space-y-3 text-sm text-text-muted leading-relaxed">
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-brand/20 text-brand flex items-center justify-center text-xs font-bold">1</span>
              <span><a href="/login" className="text-brand hover:underline font-medium">Sign up or log in</a> with your Google account</span>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-brand/20 text-brand flex items-center justify-center text-xs font-bold">2</span>
              <span>Go to your <a href="/dashboard" className="text-brand hover:underline font-medium">Dashboard</a></span>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-brand/20 text-brand flex items-center justify-center text-xs font-bold">3</span>
              <span>Enter a name for your key (e.g. "my-app-dev") and click <strong className="text-text">Create</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-brand/20 text-brand flex items-center justify-center text-xs font-bold">4</span>
              <span><strong className="text-text">Copy the key immediately</strong> — it starts with <code className="text-xs bg-surface px-1 py-0.5 rounded">sk_free_</code> and is only shown once</span>
            </li>
          </ol>
        </div>
      </section>

      {/* Configuration */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="configuration">
          Configuration
        </h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium text-text-muted mb-2">Claude Desktop</h3>
            <CodeBlock code={CLAUDE_CONFIG} title="claude_desktop_config.json" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-muted mb-2">Cursor</h3>
            <CodeBlock code={CURSOR_CONFIG} title=".cursor/mcp.json" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-muted mb-2">VS Code</h3>
            <CodeBlock code={VSCODE_CONFIG} title="User Settings (JSON)" />
          </div>
        </div>
      </section>

      {/* Direct REST API */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="rest-api">
          Direct REST API
        </h2>
        <p className="text-sm text-text-muted mb-4">
          You can also call the API directly with any HTTP client. All endpoints accept GET requests with an{" "}
          <code className="text-xs bg-surface px-1 py-0.5 rounded">Authorization: Bearer</code> header.
        </p>
        <CodeBlock
          code={`curl "https://mcp-api.starsinger.ai/api/mcp/search?q=chill+jazz" \\
  -H "Authorization: Bearer sk_free_your_key_here"`}
          title="Example: Search with curl"
        />
        <p className="text-sm text-text-muted mt-4">
          <Link to="/playground" className="text-brand hover:underline font-medium">Try it in the Playground</Link> — test any endpoint right in your browser.
        </p>
      </section>

      {/* Environment Variables */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="env-vars">
          Environment Variables
        </h2>
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-lighter text-text-muted">
                <th className="text-left px-4 py-3">Variable</th>
                <th className="text-left px-4 py-3">Required</th>
                <th className="text-left px-4 py-3">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="px-4 py-3 font-mono text-xs">STARSINGER_API_KEY</td>
                <td className="px-4 py-3 text-green-400">Yes</td>
                <td className="px-4 py-3 text-text-muted">Your StarSinger API key</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-xs">STARSINGER_API_URL</td>
                <td className="px-4 py-3 text-text-muted">No</td>
                <td className="px-4 py-3 text-text-muted">
                  API base URL (default: mcp-api.starsinger.ai)
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Tools Reference */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-6" id="tools">
          Catalog Tools Reference
        </h2>
        <p className="text-sm text-text-muted mb-6">
          search_music and similar_music are on the hosted server and in the npm package; the others are npm package and
          REST only.
        </p>
        <div className="space-y-8">
          {tools.map((tool) => (
            <div key={tool.name} className="border border-border rounded-xl p-5 bg-surface-light">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-mono text-brand font-semibold">{tool.name}</h3>
                <Link to="/playground" className="text-xs text-brand hover:underline">
                  Try in Playground
                </Link>
              </div>
              <p className="text-sm text-text-muted mb-4">{tool.description}</p>
              <div className="border border-border rounded-lg overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-lighter text-text-muted text-xs">
                      <th className="text-left px-3 py-2">Parameter</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Required</th>
                      <th className="text-left px-3 py-2">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {tool.args.map((arg) => (
                      <tr key={arg.name}>
                        <td className="px-3 py-2 font-mono text-xs">{arg.name}</td>
                        <td className="px-3 py-2 text-xs text-text-muted">{arg.type}</td>
                        <td className="px-3 py-2 text-xs">
                          {arg.required ? (
                            <span className="text-green-400">yes</span>
                          ) : (
                            <span className="text-text-muted">no</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-text-muted">{arg.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* curl example */}
              <div className="mb-3">
                <p className="text-xs text-text-muted mb-1.5">curl example</p>
                <CodeBlock code={tool.curlExample} title="" />
              </div>

              {/* Response example */}
              <div>
                <p className="text-xs text-text-muted mb-1.5">Example response</p>
                <CodeBlock code={tool.responseExample} title="" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Moods Reference */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="moods">
          Moods Reference
        </h2>
        <p className="text-sm text-text-muted mb-3">
          Use <code className="text-xs bg-surface px-1 py-0.5 rounded">GET /api/mcp/moods</code> to get the full list, or filter search results by mood.
        </p>
        <CodeBlock code={MOODS_EXAMPLE} title="GET /api/mcp/moods" />
      </section>

      {/* Response Format */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="responses">
          Response Format
        </h2>
        <p className="text-sm text-text-muted mb-4">
          Every track returned by the API includes these fields:
        </p>
        <CodeBlock
          language="typescript"
          code={`{
  id: string           // Unique track ID
  title: string        // Display title
  songTitle: string    // Song title
  artistName: string   // Artist name
  genre: string | null // Genre tag
  mood: string | null  // Mood category ID
  audioUrl: string     // Direct MP3 streaming URL
  thumbnailUrl: string // Cover art URL
  lyrics: string       // Full lyrics text
  language: string     // Language code (en, ja, fr, etc.)
  views: number        // Play count
  likes: number        // Like count
  source: "StarSinger" // Attribution
  createYourOwn: "https://starsinger.ai"
}`}
          title="Track object"
        />
      </section>

      {/* Playlists API */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-6" id="playlists">
          Playlists API
        </h2>
        <p className="text-sm text-text-muted mb-6">
          Create and manage curated playlists of tracks. Perfect for DJ sets, background music schedules, or AI-curated listening experiences.
        </p>

        {/* MCP Tools */}
        <div className="border border-border rounded-xl p-5 bg-surface-light mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-mono text-brand font-semibold">create_playlist</h3>
            <Link to="/playground" className="text-xs text-brand hover:underline">Try in Playground</Link>
          </div>
          <p className="text-sm text-text-muted mb-3">Create a new playlist with tracks from search results.</p>
          <CodeBlock
            code={`curl -X POST "https://mcp-api.starsinger.ai/api/mcp/playlists" \\
  -H "Authorization: Bearer sk_free_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Morning Cafe Jazz",
    "description": "Chill jazz for the morning crowd",
    "trackIds": ["TRACK_ID_1", "TRACK_ID_2", "TRACK_ID_3"],
    "tags": ["chill", "morning", "jazz"],
    "isPublic": true
  }'`}
            title="POST /api/mcp/playlists"
          />
          <div className="mt-3">
            <p className="text-xs text-text-muted mb-1.5">Example response</p>
            <CodeBlock
              code={`{
  "id": "pl_abc123def456",
  "name": "Morning Cafe Jazz",
  "description": "Chill jazz for the morning crowd",
  "trackCount": 3,
  "createdAt": 1711843200000
}`}
              title=""
            />
          </div>
        </div>

        <div className="border border-border rounded-xl p-5 bg-surface-light mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-mono text-brand font-semibold">get_playlist</h3>
            <Link to="/playground" className="text-xs text-brand hover:underline">Try in Playground</Link>
          </div>
          <p className="text-sm text-text-muted mb-3">Get a playlist with all its tracks resolved to full objects with streaming URLs.</p>
          <CodeBlock
            code={`curl "https://mcp-api.starsinger.ai/api/mcp/playlist?id=PLAYLIST_ID" \\
  -H "Authorization: Bearer sk_free_..."`}
            title="GET /api/mcp/playlist?id=..."
          />
        </div>

        {/* Other REST endpoints */}
        <div className="space-y-4">
          <div className="border border-border rounded-xl p-5 bg-surface-light">
            <h3 className="font-mono text-brand font-semibold mb-2">GET /api/mcp/playlists</h3>
            <p className="text-sm text-text-muted mb-3">List your playlists. Filter by tag.</p>
            <CodeBlock
              code={`curl "https://mcp-api.starsinger.ai/api/mcp/playlists?tag=chill&limit=20" \\
  -H "Authorization: Bearer sk_free_..."`}
              title=""
            />
          </div>

          <div className="border border-border rounded-xl p-5 bg-surface-light">
            <h3 className="font-mono text-brand font-semibold mb-2">GET /api/mcp/playlists/public</h3>
            <p className="text-sm text-text-muted mb-3">Browse public playlists created by the community.</p>
            <CodeBlock
              code={`curl "https://mcp-api.starsinger.ai/api/mcp/playlists/public?tag=morning" \\
  -H "Authorization: Bearer sk_free_..."`}
              title=""
            />
          </div>

          <div className="border border-border rounded-xl p-5 bg-surface-light">
            <h3 className="font-mono text-brand font-semibold mb-2">PATCH /api/mcp/playlist?id=...</h3>
            <p className="text-sm text-text-muted mb-3">Update a playlist: rename, reorder tracks, add/remove tracks, change tags.</p>
            <CodeBlock
              code={`curl -X PATCH "https://mcp-api.starsinger.ai/api/mcp/playlist?id=PLAYLIST_ID" \\
  -H "Authorization: Bearer sk_free_..." \\
  -H "Content-Type: application/json" \\
  -d '{ "name": "Updated Name", "trackIds": ["ID_3", "ID_1", "ID_2"] }'`}
              title=""
            />
          </div>

          <div className="border border-border rounded-xl p-5 bg-surface-light">
            <h3 className="font-mono text-brand font-semibold mb-2">DELETE /api/mcp/playlist?id=...</h3>
            <p className="text-sm text-text-muted mb-3">Delete a playlist you own.</p>
            <CodeBlock
              code={`curl -X DELETE "https://mcp-api.starsinger.ai/api/mcp/playlist?id=PLAYLIST_ID" \\
  -H "Authorization: Bearer sk_free_..."`}
              title=""
            />
          </div>
        </div>

        {/* Playlist limits */}
        <div className="border border-border rounded-xl overflow-hidden mt-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-lighter text-text-muted">
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Max Playlists</th>
                <th className="text-left px-4 py-3">Max Tracks/Playlist</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr><td className="px-4 py-3">Free</td><td className="px-4 py-3">5</td><td className="px-4 py-3">50</td></tr>
              <tr><td className="px-4 py-3">Pro</td><td className="px-4 py-3">50</td><td className="px-4 py-3">200</td></tr>
              <tr><td className="px-4 py-3">Business</td><td className="px-4 py-3">500</td><td className="px-4 py-3">500</td></tr>
              <tr><td className="px-4 py-3">Enterprise</td><td className="px-4 py-3">5,000</td><td className="px-4 py-3">1,000</td></tr>
            </tbody>
          </table>
        </div>

        {/* Workflow example */}
        <div className="mt-6 border border-border rounded-xl p-5 bg-surface-light">
          <h3 className="font-semibold text-sm mb-3">Example: Build a Cafe Playlist</h3>
          <ol className="space-y-2 text-sm text-text-muted">
            <li><strong className="text-text">1. Search for tracks:</strong> Ask your AI agent "Find me 10 chill jazz piano tracks"</li>
            <li><strong className="text-text">2. Create playlist:</strong> "Create a playlist called 'Morning Cafe' with those tracks"</li>
            <li><strong className="text-text">3. Retrieve later:</strong> "Get my Morning Cafe playlist" returns all tracks with streaming URLs</li>
            <li><strong className="text-text">4. Update as needed:</strong> Add/remove tracks, reorder, or rename via PATCH</li>
          </ol>
        </div>
      </section>

      {/* Rate Limits */}
      <section className="mb-14">
        <h2 className="text-xl font-semibold mb-4" id="rate-limits">
          Rate Limits
        </h2>
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-lighter text-text-muted">
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Daily Limit</th>
                <th className="text-left px-4 py-3">API Keys</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="px-4 py-3">Free</td>
                <td className="px-4 py-3">100</td>
                <td className="px-4 py-3">1</td>
              </tr>
              <tr>
                <td className="px-4 py-3">Pro ($29/mo)</td>
                <td className="px-4 py-3">1,000</td>
                <td className="px-4 py-3">5</td>
              </tr>
              <tr>
                <td className="px-4 py-3">Business ($99/mo)</td>
                <td className="px-4 py-3">10,000</td>
                <td className="px-4 py-3">20</td>
              </tr>
              <tr>
                <td className="px-4 py-3">Enterprise</td>
                <td className="px-4 py-3">50,000</td>
                <td className="px-4 py-3">50</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-text-muted mt-3">
          Limits reset daily at midnight UTC. Exceeding your limit returns HTTP 429 with a <code className="bg-surface px-1 py-0.5 rounded">Retry-After</code> header.{" "}
          <Link to="/pricing" className="text-brand hover:underline">View pricing</Link>
        </p>
      </section>
      </div>
    </>
  );
}
